import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

/**
 * PolicyTemplatesHelpModal - Feature 031
 * Quick-reference popup explaining the Policy Templates screen (Templates + Variables tabs)
 * and, importantly, how both connect to Credentials Management. Purely informational; no
 * data mutation.
 */
const PolicyTemplatesHelpModal = ({ isOpen, onClose, onStartTutorial }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Policy Templates Management — Quick Guide</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close help">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5 text-sm text-gray-700">
          <p>
            This screen replaces the manual Postman procedure (PROC036) for managing the platform&apos;s permission
            templates and variables. It is directly connected to <strong>Credentials Management</strong>: templates
            you create or edit here are exactly the ones an admin picks from when adding a new credential or editing
            an existing one&apos;s <strong>Permissions</strong>.
          </p>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Templates tab</h3>
            <p>
              A template groups a description, the platform services it grants (e.g. iot, s3), and the actual
              IAM-style permission policy (JSON) behind them. <strong>Create Template</strong> starts a new one — the
              description and services are what admins see when selecting it in Credentials Management. The
              Permission Policy field is validated for both JSON syntax and structure (each statement needs a
              non-empty &quot;actions&quot; array and a &quot;resources&quot; array with a &quot;resource&quot; and
              &quot;service&quot; on each). Deleting a template is blocked by the platform if it&apos;s currently
              assigned to any credential.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Variables tab &amp; {'{{NAME}}'} placeholders</h3>
            <p>
              Variables are reusable named values (e.g. an account ID) you can reference inside a template&apos;s
              Permission Policy as a <span className="font-mono">{'{{NAME}}'}</span> placeholder, so several templates
              can share one value without repeating it. You don&apos;t have to switch tabs to create one — the
              template form has its own <strong>+ New Variable</strong> button next to the Permission Policy field,
              plus clickable chips for every existing variable that insert <span className="font-mono">{'{{NAME}}'}</span>
              {' '}at your cursor. A variable&apos;s name can&apos;t be changed after creation — delete and recreate it
              instead.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Where templates get used</h3>
            <p>
              Go to <strong>Credentials Management</strong> → <strong>Add New Credential</strong> (APIs field) or an
              existing credential&apos;s <strong>View &amp; Edit Details</strong> → <strong>Permissions</strong> section
              to see templates created here in the selection list. If a template you need isn&apos;t there yet, create
              it on this screen first.
            </p>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-between">
          {onStartTutorial && (
            <button
              onClick={onStartTutorial}
              className="px-4 py-2 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md focus:outline-none"
            >
              Start Guided Tutorial
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyTemplatesHelpModal;
